import React from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { AppShell } from "@/app/AppShell";
import { RequireAuth, RequireAdmin, RequireAdminOrOrg } from "@/app/RequireAuth";

import { DashboardPage } from "@/pages/DashboardPage";
import { UnitDetailPage } from "@/pages/UnitDetailPage";
import { SessionsPage } from "@/pages/SessionsPage";
import { BillingPage } from "@/pages/BillingPage";
import { LoadBalancingPage } from "@/pages/LoadBalancingPage";
import { MetricsPage } from "@/pages/MetricsPage";
import { CacheStatsPage } from "@/pages/CacheStatsPage";
import { SystemHealthPage } from "@/pages/SystemHealthPage";
import { MobileVersionsPage } from "@/pages/MobileVersionsPage";
import { LogsPage } from "@/pages/LogsPage";
import { EcachePage } from "@/pages/EcachePage";
import { EventLogPage } from "@/pages/EventLogPage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { MapPage } from "@/pages/MapPage";
import { UsersPage } from "@/pages/UsersPage";
import { UserDetailPage } from "@/pages/UserDetailPage";
import { DevicesPage } from "@/pages/DevicesPage";
import { LoginPage } from "@/pages/LoginPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ResetPasswordSmsPage } from "@/pages/ResetPasswordSmsPage";
import { ForbiddenPage } from "@/pages/ForbiddenPage";
import {AdminDashboardPage} from "@/pages/AdminDashboardPage";
import { OrganizationsPage } from "@/pages/OrganizationsPage";
import { OrganizationDetailPage } from "@/pages/OrganizationDetailPage";
import { AccessControlPage } from "@/pages/AccessControlPage";
import { PricingProfilesPage } from "@/pages/PricingProfilesPage";
import { EmulatorPage } from "@/pages/EmulatorPage";
import { PandoChargersPage } from "@/pages/PandoChargersPage";
import { AccountSettingsPage } from "@/pages/AccountSettingsPage";

/**
 * `/unit/:id/telemetry?from=…&to=…` → the unit page with its telemetry tab open.
 * The query string is carried over so a link can pin an exact window.
 */
function UnitTelemetryRedirect() {
  const { unitId } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", "telemetry");
  return <Navigate to={`/unit/${encodeURIComponent(unitId ?? "")}?${params.toString()}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password-sms" element={<ResetPasswordSmsPage />} />
      <Route path="/forbidden" element={<ForbiddenPage />} />

      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="account" element={<AccountSettingsPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="unit/:unitId" element={<UnitDetailPage />} />
        {/* Deep link from alerts/tickets straight into the telemetry tab. */}
        <Route path="unit/:unitId/telemetry" element={<UnitTelemetryRedirect />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="billing" element={<RequireAdmin><BillingPage /></RequireAdmin>} />
        <Route path="load-balancing" element={<RequireAdminOrOrg><LoadBalancingPage /></RequireAdminOrOrg>} />
        <Route path="metrics" element={<RequireAdmin><MetricsPage /></RequireAdmin>} />
        <Route path="cache-stats" element={<RequireAdmin><CacheStatsPage /></RequireAdmin>} />
        <Route path="system-health" element={<RequireAdmin><SystemHealthPage /></RequireAdmin>} />
        <Route path="mobile-versions" element={<RequireAdmin><MobileVersionsPage /></RequireAdmin>} />
        <Route path="logs" element={<RequireAdmin><LogsPage /></RequireAdmin>} />
        <Route path="ecache" element={<RequireAdmin><EcachePage /></RequireAdmin>} />
          <Route path="admin/dashboard" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
        <Route path="eventlog" element={<RequireAdmin><EventLogPage /></RequireAdmin>} />
        <Route path="audit" element={<RequireAdmin><AuditLogPage /></RequireAdmin>} />
        <Route path="map" element={<RequireAdmin><MapPage /></RequireAdmin>} />
        <Route path="users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
        <Route path="users/:userId" element={<RequireAdmin><UserDetailPage /></RequireAdmin>} />
        <Route path="organizations" element={<RequireAdminOrOrg><OrganizationsPage /></RequireAdminOrOrg>} />
        <Route path="organizations/:orgId" element={<RequireAdminOrOrg><OrganizationDetailPage /></RequireAdminOrOrg>} />
        <Route path="access-control" element={<RequireAdminOrOrg><AccessControlPage /></RequireAdminOrOrg>} />
        <Route path="pricing-profiles" element={<RequireAdminOrOrg><PricingProfilesPage /></RequireAdminOrOrg>} />
        {/* Back-compat: old bookmarks to /special-rates redirect to the renamed route. */}
        <Route path="special-rates" element={<Navigate to="/pricing-profiles" replace />} />
        <Route path="emulator" element={<RequireAdmin><EmulatorPage /></RequireAdmin>} />
        <Route path="pando" element={<RequireAdmin><PandoChargersPage /></RequireAdmin>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
